import URI from 'spotify-liburi';
import { roundDecimalToPercentage } from '../utils/numberHelpers';
import { contentDispositionToFilename, csvUrlFromCsv } from '../utils/dataHelpers';
import { DatePreset } from 'libs/utils/chartUtils';
import { assertSuccess, ForbiddenError, get, NotFoundError, post, put, del } from './request';
import { AuthRequestInit } from './auth';
import { QueryResult, useQuery, useQueryCache } from 'react-query';
import { mockWriterCuration } from 'mocks';
import { useInternalAdmin } from 'app/RemoteConfig/RemoteConfig';
import {
  AggregateStreams,
  BarGraphValue,
  CsvResponse,
  Playlist,
  PotentialMatch,
  RecordingEntity,
  RecordingSearchResult,
  SongwriterPageStatusResponse,
  SortOrder,
  StreamsByProduct,
  StreamsByRegion,
  Team,
  TeamResponse,
  TimeFilter,
  TimelineAnnotation,
  TimelineAnnotationsResponse,
  TopWriters,
  UserEntity,
  WorkEntity,
  WriterBasicInfo,
  WriterCatalogFilter,
  WriterCatalogParams,
  WriterCurationResponse,
  WriterEntity,
  WriterResponse,
  WrittenByPlaylist,
  AllWrittenByPlaylistResponse,
} from './s4pTypes';
import { getTimelineStreams, parseTimelineAnnotations } from './timeline';
import { useT } from '@spotify-internal/i18n-core';
import { S4P_BACKEND_URL } from './environment';

export const teamViewPermission = 'team.view';
export const teamInvitePermission = 'team.invite';
export const catalogViewPermission = 's4p.catalog.view';
export const searchPermission = 's4p.catalog.search';
export const viewSongwriterPagePermission = 'songwriterpage.view';

export const BACKEND_URL = S4P_BACKEND_URL;

export type WithRefetch = { refetch: () => void };

export const requestOptions: AuthRequestInit = { mode: 'cors' };

export function normalizeWriters(writers: WriterBasicInfo[]): WriterBasicInfo[] {
  return writers.reduce<WriterBasicInfo[]>((filtered, writer) => {
    if (!filtered.some((w) => w.ipi === writer.ipi && w.name === writer.name)) {
      filtered.push(writer);
    }

    return filtered;
  }, []);
}

const uriToGid = (trackUri: string): string => URI.idToHex(trackUri.replace('spotify:track:', ''));

export const transformToCsvResponse = (parsedCsv: string, response: Response) => ({
  csvUrl: csvUrlFromCsv(parsedCsv),
  filename: contentDispositionToFilename(response),
});

const reformatAgeGroups = (label: string, t: ReturnType<typeof useT>) => {
  switch (label) {
    case 'Age0To17':
      return t('L_AGEGROUP_0', '<18', 'Age group - Under 18');
    case 'Age18To24':
      return t('L_AGEGROUP_1', '18-24', 'Age group - 18 to 24');
    case 'Age25To34':
      return t('L_AGEGROUP_2', '25-34', 'Age group - 25 to 34');
    case 'Age35To44':
      return t('L_AGEGROUP_3', '35-44', 'Age group - 35 to 44');
    case 'Age45To54':
      return t('L_AGEGROUP_4', '45-54', 'Age group - 45 to 54');
    case 'Age55To64':
      return t('L_AGEGROUP_5', '55-64', 'Age group - 55 to 64');
    case 'Age65Plus':
      return t('L_AGEGROUP_6', '65+', 'Age group - 65 and over');
    case 'AgeUnknown':
      return t('L_AGEGROUP_7', 'unknown', 'Age group - age is unknown');
    default:
      return label;
  }
};

const reformatGenderGroups = (label: string, t: ReturnType<typeof useT>) => {
  switch (label) {
    case 'neutral':
      return t('L_GENDERGROUP_0', 'non-binary', 'Gender group - non-binary');
    case 'unknown':
      return t('L_GENDERGROUP_1', 'not specified', 'Gender group - gender is not specified');
    case 'female':
      return t('L_GENDERGROUP_2', 'female', 'Gender group - female');
    case 'male':
      return t('L_GENDERGROUP_3', 'male', 'Gender group - male');
    default:
      return label;
  }
};

export const parseDemographics = (
  data: AggregateStreams,
  type: 'age' | 'gender',
  t: ReturnType<typeof useT>,
): BarGraphValue[] => {
  const parsedData: BarGraphValue[] = [];
  Object.keys(data.percentages)
    .sort()
    .forEach((dataPoint) => {
      parsedData.push({
        key: dataPoint,
        label:
          type === 'age' ? reformatAgeGroups(dataPoint, t) : reformatGenderGroups(dataPoint, t),
        value: roundDecimalToPercentage(data.percentages[dataPoint]),
      });
    });

  return parsedData;
};

export const getCurrentUser = async (): Promise<UserEntity> => {
  const response = await get(`${BACKEND_URL}/v3/currentUser`, requestOptions);

  assertSuccess(response);

  return await response.json();
};

export function useCurrentUser() {
  const response = useQuery(['current-user'], () => getCurrentUser());
  return response.data!;
}

export async function getCurrentTeams(): Promise<Team[]> {
  const response = await get(`${BACKEND_URL}/v3/teams/initial`, requestOptions);

  assertSuccess(response);

  return await response.json();
}

export function useCurrentTeams() {
  const response = useQuery(['current-teams'], () => getCurrentTeams());
  return response.data!;
}

export async function getTeamMembers(): Promise<TeamResponse> {
  const response = await get(`${BACKEND_URL}/v3/team`, requestOptions);

  if (!response.ok) {
    throw new Error('Failed loading the current user’s team');
  }

  return response.json();
}

export const searchWorks = async (query: string): Promise<WorkEntity[]> => {
  const parsedQuery = query.replace(/spotify:track:[a-z|A-Z|0-9]{22}/g, uriToGid);
  const params = new URLSearchParams();
  params.set('search-term', parsedQuery);
  const response = await get(`${BACKEND_URL}/v3/works/search/?${params}`, requestOptions);

  if (response.status === 403) {
    throw new ForbiddenError();
  }

  if (!response.ok) {
    throw new Error('Failed searching for works');
  }

  const data: WorkEntity[] = await response.json();

  data.forEach((work) => {
    work.writers = normalizeWriters(work.writers);
  });

  return data;
};

export const searchWriters = async (query: string): Promise<WriterEntity[]> => {
  const params = new URLSearchParams();
  params.set('search-term', query);
  const response = await get(`${BACKEND_URL}/v3/writer/search/?${params}`, requestOptions);

  if (!response.ok) {
    throw new Error('Failed searching for writers');
  }

  return await response.json();
};

export const searchRecordings = async (query: string): Promise<RecordingSearchResult[]> => {
  const params = new URLSearchParams();
  params.set('search-term', query);
  const response = await get(`${BACKEND_URL}/v3/recordings/search/?${params}`, requestOptions);

  if (response.status === 403) {
    throw new ForbiddenError();
  }

  if (!response.ok) {
    throw new Error('Failed searching for recordings');
  }

  return await response.json();
};

export const searchTeams = async (name: string): Promise<Team[]> => {
  const response = await get(
    `${BACKEND_URL}/v3/team/search?name=${encodeURIComponent(name)}`,
    requestOptions,
  );
  return await response.json();
};

export const getWork = async (workGid: string): Promise<WorkEntity> => {
  const response = await get(`${BACKEND_URL}/v3/work/${workGid}`, requestOptions);

  assertSuccess(response);

  const work: WorkEntity = await response.json();
  work.writers = normalizeWriters(work.writers);

  return work;
};

export function useWork(workGid: string): WorkEntity {
  const response = useQuery(['workEntity', workGid], () => getWork(workGid));
  return response.data!;
}

export function useWorkCache(workGid: string): (updated: WorkEntity) => void {
  const queryCache = useQueryCache();
  return (updated) => {
    queryCache.setQueryData(['workEntity', workGid], updated);
    queryCache.refetchQueries((query) =>
      query.queryKey.some((v) => typeof v === 'string' && v.includes(workGid)),
    );
  };
}

type WorkCountResponse = {
  numberOfWorks: number;
};

const getNumberOfWorks = async (country: string): Promise<WorkCountResponse> => {
  const response = await get(`${BACKEND_URL}/v3/works/count?country=${country}`, requestOptions);
  return await response.json();
};

export function useNumberOfWorks(country: string): WorkCountResponse {
  const workCountResponse = useQuery(['numberOfWorks', country], () => getNumberOfWorks(country));
  return workCountResponse.data!;
}

export const getSongwriterPageStatus = async (
  ipi: string,
): Promise<SongwriterPageStatusResponse | null> => {
  const response = await get(`${BACKEND_URL}/v3/writer/${ipi}/songwriter-page`);

  if (response.status === 404) {
    return null;
  } else if (!response.ok) {
    throw new Error('Failed to load songwriter page status');
  }

  return response.json();
};

export type GetWriterParams = {
  page: number;
  sortKey: string;
  sortOrder: SortOrder;
  pageSize: number;
  ipi: string;
  worksQuery: string;
};

export const getWriter = async ({
  ipi,
  sortKey,
  sortOrder,
  pageSize,
  page,
  worksQuery,
}: GetWriterParams): Promise<WriterResponse> => {
  // ElasticSearch orders a descending string sort from Z -> A, while our UI uses the opposite; we switch the sortOrder sent to the request when sorting by title
  let actualSortOrder = sortOrder;
  if (sortKey === 'title') {
    actualSortOrder = sortOrder === 'DESC' ? 'ASC' : 'DESC';
  }

  const params = `results-per-page=${pageSize}&page=${page}&sort-by=${sortKey}&sort-order=${actualSortOrder}&works-query=${worksQuery}`;
  const response = await get(`${BACKEND_URL}/v3/writer/${ipi}?${params}`, requestOptions);

  assertSuccess(response);

  if (!response.ok) {
    throw new Error('Failed to load writer details');
  }

  return await response.json();
};

export const useWriter = ({
  ipi,
  sortKey,
  sortOrder,
  pageSize,
  page,
  worksQuery,
}: GetWriterParams) => {
  const writer = useQuery(['writerWorks', ipi, sortKey, sortOrder, page, worksQuery], () =>
    getWriter({ ipi, sortKey, sortOrder, pageSize, page, worksQuery }),
  );

  return writer.data!;
};

export const getWriterWorksCsv = async (
  writerIpi: string,
  sortKey: string,
  sortOrder: SortOrder,
  numResults: number,
  page: number,
): Promise<CsvResponse> => {
  const response = await get(
    `${BACKEND_URL}/v3/writer/${writerIpi}/download-csv?sort-by=${sortKey}&sort-order=${sortOrder}&results-per-page=${numResults}&page=${page}`,
    requestOptions,
  );

  const parsedCsv = await response.text();

  return transformToCsvResponse(parsedCsv, response);
};

export const getWriterTimelineStreams = async (
  ipi: string,
  startDate: string,
  endDate: string,
  onErrorCallback: Function,
): Promise<StreamsByRegion> => {
  return getTimelineStreams(
    `/v3/writer/${ipi}/timeline/streams`,
    startDate,
    endDate,
    onErrorCallback,
  );
};

export const getWriterTimelineStreamsCsv = async (
  ipi: string,
  startDate: string,
  endDate: string,
  comparedCountries: string[],
): Promise<CsvResponse> => {
  const parameters = `start-date=${startDate}&end-date=${endDate}&compared-countries=${comparedCountries.join()}`;
  const response = await get(
    `${BACKEND_URL}/v3/writer/${ipi}/timeline/streams/download-csv?${parameters}`,
    requestOptions,
  );
  assertSuccess(response);
  const parsedCsv = await response.text();

  return transformToCsvResponse(parsedCsv, response);
};

export const getWriterCurationForCreator = async (
  creatorUri: string,
): Promise<WriterCurationResponse> => {
  const response = await get(`${BACKEND_URL}/v3/creator/${creatorUri}/curation`);

  assertSuccess(response);
  return response.json();
};

export function useCuration(creatorUri: string): WriterCurationResponse & WithRefetch {
  const internalAdmin = useInternalAdmin();
  const useTestData = internalAdmin && window.location.search.indexOf('test_curation_data') > -1;

  const { data, refetch } = useQuery(['songwriterPageCurationForCreator', creatorUri], () =>
    getWriterCurationForCreator(creatorUri),
  );

  return {
    ...data!,
    ...(useTestData
      ? {
          collaborators: mockWriterCuration.collaborators,
          latestRelease: mockWriterCuration.latestRelease,
        }
      : {}),
    refetch,
  }!;
}

export async function updateDisplayName(creatorUri: string, displayName: string): Promise<void> {
  const response = await post(`${BACKEND_URL}/v3/creator/${creatorUri}/display-name`, {
    body: JSON.stringify({
      displayName,
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to save display name');
  }
}

export const hideWorkForSongwriterPage = async (
  creatorUri: string,
  workGid: string,
): Promise<boolean> => {
  const response = await post(`${BACKEND_URL}/v3/creator/${creatorUri}/work/${workGid}/hide`);

  if (!response.ok) {
    throw new Error('The request failed, please try again later');
  }
  return true;
};

export const showWorkForSongwriterPage = async (
  creatorUri: string,
  workGid: string,
): Promise<boolean> => {
  const response = await post(`${BACKEND_URL}/v3/creator/${creatorUri}/work/${workGid}/unhide`);

  if (!response.ok) {
    throw new Error('The request failed, please try again later');
  }
  return true;
};

export const publishSongwriterPage = async (creatorUri: string): Promise<boolean> => {
  const response = await post(`${BACKEND_URL}/v3/creator/${creatorUri}/publish`);

  if (!response.ok) {
    throw new Error('The request failed, please try again later');
  }
  return true;
};

export const unpublishSongwriterPage = async (creatorUri: string): Promise<boolean> => {
  const response = await post(`${BACKEND_URL}/v3/creator/${creatorUri}/unpublish`);

  if (!response.ok) {
    throw new Error('The request failed, please try again later');
  }
  return true;
};

export const saveSongwriterImage = async (
  creatorUri: string,
  uploadToken: string,
): Promise<void> => {
  const response = await post(`${BACKEND_URL}/v3/creator/${creatorUri}/save-songwriter-image`, {
    body: JSON.stringify({
      token: uploadToken,
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to save songwriter image');
  }
};

export const getWorkTimelineStreams = async (
  workGid: string,
  startDate: string,
  endDate: string,
  onErrorCallback: Function,
): Promise<StreamsByRegion> => {
  return getTimelineStreams(
    `/v3/work/${workGid}/timeline/streams`,
    startDate,
    endDate,
    onErrorCallback,
  );
};

const getWorkTimelineStreamsCsv = async (
  workGid: string,
  startDate: string,
  endDate: string,
  comparedCountries: string[],
): Promise<CsvResponse> => {
  const parameters = `start-date=${startDate}&end-date=${endDate}&compared-countries=${comparedCountries.join()}`;
  const response = await get(
    `${BACKEND_URL}/v3/work/${workGid}/timeline/streams/download-csv?${parameters}`,
    requestOptions,
  );
  assertSuccess(response);
  const parsedCsv = await response.text();

  return transformToCsvResponse(parsedCsv, response);
};

export const getWorkTimelineAnnotations = async (
  workGid: string,
  timeFilter: TimeFilter,
): Promise<TimelineAnnotation[]> => {
  const response = await get(
    `${BACKEND_URL}/v3/work/${workGid}/timeline/annotation?time-filter=${timeFilter}`,
    {
      headers: {
        Accept: 'application/json',
      },
    },
  );

  if (!response.ok) {
    throw new Error('Failed to load work timeline annotations');
  }

  if (response.status === 204) {
    return [];
  }

  const annotations: TimelineAnnotationsResponse = await response.json();

  return parseTimelineAnnotations(annotations);
};

export const getRecording = async (
  workGid: string,
  recordingGid: string,
): Promise<RecordingEntity> => {
  const response = await get(
    `${BACKEND_URL}/v3/work/${workGid}/recording/${recordingGid}`,
    requestOptions,
  );

  assertSuccess(response);

  return response.json();
};

const getRecordingTimelineStreams = async (
  workGid: string,
  recordingGid: string,
  startDate: string,
  endDate: string,
): Promise<StreamsByRegion> => {
  return getTimelineStreams(
    `/v3/work/${workGid}/recording/${recordingGid}/timeline/streams`,
    startDate,
    endDate,
  );
};

const getRecordingTimelineStreamsCsv = async (
  workGid: string,
  recordingGid: string,
  startDate: string,
  endDate: string,
  comparedCountries: string[],
): Promise<CsvResponse> => {
  const parameters = `start-date=${startDate}&end-date=${endDate}&compared-countries=${comparedCountries.join()}`;
  const response = await get(
    `${BACKEND_URL}/v3/work/${workGid}/recording/${recordingGid}/timeline/streams/download-csv?${parameters}`,
    requestOptions,
  );
  assertSuccess(response);

  const parsedCsv = await response.text();

  return transformToCsvResponse(parsedCsv, response);
};

export type GetSortedWorksOptions = {
  sortOrder: SortOrder;
  numResults: number;
  page: number;
  country: string;
  datePreset: DatePreset;
};

export const getSortedWorks = async ({
  sortOrder,
  numResults,
  page,
  country,
  datePreset,
}: GetSortedWorksOptions): Promise<WorkEntity[]> => {
  const params = new URLSearchParams();
  params.set('sort-order', sortOrder);
  params.set('results-per-page', String(numResults));
  params.set('time-filter', datePreset);
  params.set('page', String(page));
  params.set('country', country);

  const response = await get(`${BACKEND_URL}/v3/works?${params}`, requestOptions);

  if (response.status === 403) {
    throw new ForbiddenError();
  }

  if (!response.ok) {
    throw new Error('Failed to load works');
  }

  const works: WorkEntity[] = await response.json();

  works.forEach((work) => {
    work.writers = normalizeWriters(work.writers);
  });

  return works;
};

export function useSortedWorks(params: GetSortedWorksOptions): WorkEntity[] {
  const worksResponse = useQuery(['sortedWorks', params], () => getSortedWorks(params));
  return worksResponse.data!;
}

export const getCatalogCsv = async (
  sortOrder: SortOrder,
  numResults: number,
  page: number,
  country: string,
  datePreset: string,
): Promise<CsvResponse> => {
  const response = await get(
    `${BACKEND_URL}/v3/works/download-csv?sort-order=${sortOrder}&results-per-page=${numResults}&page=${page}&country=${country}&time-filter=${datePreset}`,
    requestOptions,
  );

  if (response.status === 403) {
    throw new ForbiddenError();
  }

  const parsedCsv = await response.text();
  return transformToCsvResponse(parsedCsv, response);
};

const getRecordingAgeData = async (
  workGid: string,
  recordingGid: string,
  startDate: string,
  endDate: string,
  t: ReturnType<typeof useT>,
): Promise<BarGraphValue[]> => {
  const parameters = `start-date=${startDate}&end-date=${endDate}`;
  const response = await get(
    `${BACKEND_URL}/v3/work/${workGid}/recording/${recordingGid}/aggregate/streams/age?${parameters}`,
    requestOptions,
  );

  if (response.status === 204 || response.status === 404) {
    throw new NotFoundError();
  }

  const parsedResponse = await response.json();
  return parseDemographics(parsedResponse, 'age', t);
};

const getRecordingGenderData = async (
  workGid: string,
  recordingGid: string,
  startDate: string,
  endDate: string,
  t: ReturnType<typeof useT>,
): Promise<BarGraphValue[]> => {
  const parameters = `start-date=${startDate}&end-date=${endDate}`;
  const response = await get(
    `${BACKEND_URL}/v3/work/${workGid}/recording/${recordingGid}/aggregate/streams/gender?${parameters}`,
    requestOptions,
  );

  if (response.status === 204 || response.status === 404) {
    throw new NotFoundError();
  }

  const parsedResponse = await response.json();
  return parseDemographics(parsedResponse, 'gender', t);
};

export const getTopPlaylists = async (workGid: string): Promise<Playlist[]> => {
  const parameters = 'time-filter=28day'; // currently the only option we want to show
  const response = await get(
    `${BACKEND_URL}/v3/work/${workGid}/top-playlists?${parameters}`,
    requestOptions,
  );
  if (response.status !== 200) {
    throw new Error(response.status.toString());
  }

  return await response.json();
};

export const getTopPlaylistsCsv = async (workGid: string): Promise<CsvResponse> => {
  const parameters = 'time-filter=28day'; // currently the only option we want to show
  const response = await get(
    `${BACKEND_URL}/v3/work/${workGid}/top-playlists/download-csv?${parameters}`,
    requestOptions,
  );
  if (response.status !== 200) throw new Error(response.status.toString());

  const parsedCsv = await response.text();
  return transformToCsvResponse(parsedCsv, response);
};

export const getWriterCatalog = async ({
  paging,
  sorting,
  filter,
}: WriterCatalogParams): Promise<TopWriters> => {
  const response = await get(
    `${BACKEND_URL}/v3/writers?results-per-page=${paging.resultsPerPage}&page=${paging.page}&sort-by=${sorting.key}&sort-order=${sorting.order}&control-status=${filter}`,
  );

  if (response.status === 403) {
    throw new ForbiddenError();
  }

  if (!response.ok) {
    throw new Error('Error fetching songwriters');
  }

  return await response.json();
};

export function useWriterCatalog(writerCatalogParams: WriterCatalogParams) {
  const result = useQuery(['writerCatalog', writerCatalogParams], () =>
    getWriterCatalog(writerCatalogParams),
  );
  return result.data!;
}

export const getWriterCatalogCsv = async ({
  paging,
  sorting,
  filter,
}: WriterCatalogParams): Promise<CsvResponse> => {
  const response = await get(
    `${BACKEND_URL}/v3/writers/download-csv?results-per-page=${paging.resultsPerPage}&page=${paging.page}&sort-by=${sorting.key}&sort-order=${sorting.order}&control-status=${filter}`,
  );

  if (response.status === 403) {
    throw new ForbiddenError();
  }

  const parsedCsv = await response.text();

  return transformToCsvResponse(parsedCsv, response);
};

type WriterCountResponse = {
  numberOfWriters: number;
};

export async function searchTopWritersWithSongwriterPage(searchTerm: string): Promise<TopWriters> {
  const response = await get(
    `${BACKEND_URL}/v3/writers?control-status=with-songwriter-page&search-term=${searchTerm}`,
  );

  if (!response.ok) {
    throw new Error('Error fetching songwriters');
  }

  return await response.json();
}

export async function getTopWritersWithSongwriterPage(): Promise<TopWriters> {
  const response = await get(`${BACKEND_URL}/v3/writers?control-status=with-songwriter-page`);

  if (!response.ok) {
    throw new Error('Error fetching songwriters');
  }

  return await response.json();
}

export function useTopWritersWithSongwriterPage() {
  const result = useQuery([], () => getTopWritersWithSongwriterPage());
  return result.data!;
}

const getNumberOfWriters = async (filter: WriterCatalogFilter): Promise<WriterCountResponse> => {
  const response = await get(`${BACKEND_URL}/v3/writers/count?control-status=${filter}`);

  if (!response.ok) {
    throw new Error('Error fetching songwriters');
  }

  return await response.json();
};

export function useNumberOfWriters(filter: WriterCatalogFilter) {
  const result = useQuery(['numberOfWriters', filter], () => getNumberOfWriters(filter));
  return result.data!;
}

export const getStreamsByProductForWork = async (
  workGid: string,
  startDate: string,
  endDate: string,
): Promise<StreamsByProduct> => {
  const params = new URLSearchParams();
  params.set('start-date', startDate);
  params.set('end-date', endDate);
  const response = await get(
    `${BACKEND_URL}/v3/work/${workGid}/aggregate/streams/product?${params}`,
  );

  if (response.status === 204 || response.status === 404) {
    throw new NotFoundError();
  }

  if (!response.ok) {
    throw new Error('Error fetching streams by product');
  }

  return await response.json();
};

export const getTrackEligibility = async (
  trackUri: string,
  workGid: string,
): Promise<PotentialMatch> => {
  const response = await get(
    `${BACKEND_URL}/v3/work/${workGid}/suggestions/eligibility?track=${trackUri}`,
  );

  if (!response.ok) {
    if (response.status === 400) {
      throw new Error('Invalid URI');
    } else {
      throw new Error('The request failed, please try again later');
    }
  }
  return await response.json();
};

export const getWrittenByPlaylist = async (creatorUri: string): Promise<WrittenByPlaylist> => {
  const response = await get(`${BACKEND_URL}/v3/creator/${creatorUri}/playlist`, requestOptions);

  assertSuccess(response);

  return response.json();
};

export function useWrittenByPlaylist(
  creatorUri: string,
  dependencies: any[] = [],
): QueryResult<WrittenByPlaylist> {
  return useQuery(['writtenByPlaylist', creatorUri, ...dependencies], () =>
    getWrittenByPlaylist(creatorUri),
  );
}

export const clearUnreadErrorReports = async () => {
  assertSuccess(await put(`${BACKEND_URL}/v1/error-report/clear-unread`));
};

export const getUnreadErrorReportStatus = async (): Promise<boolean> => {
  const response = await get(`${BACKEND_URL}/v1/error-report/unread`);
  if (response.status === 200) {
    return true;
  } else if (response.status === 204) {
    return false;
  } else {
    throw new Error('Cannot retrieve unread error report status.');
  }
};

export async function addPublisherWriter(
  creatorUri: string,
  publisherUri: string,
  writerIpi: string,
): Promise<void> {
  const response = await post(`${BACKEND_URL}/v3/creator/${creatorUri}/publisher-writers`, {
    body: JSON.stringify({
      publisherUri: publisherUri,
      writerIpi: writerIpi,
    }),
  });
  assertSuccess(response);
}

export async function removePublisherWriter(
  creatorUri: string,
  publisherUri: string,
  writerIpi: string,
): Promise<void> {
  const response = await del(
    `${BACKEND_URL}/v3/creator/${creatorUri}/publisher-writers/${publisherUri}/${writerIpi}`,
  );
  assertSuccess(response);
}

const getAllWrittenByPlaylists = async (): Promise<AllWrittenByPlaylistResponse> => {
  const response = await get(`${BACKEND_URL}/v1/admin/all-written-by-playlists`, requestOptions);

  assertSuccess(response);

  return response.json();
};

export function useAllWrittenByPlaylists() {
  return useQuery(['allWrittenByPlaylists'], () => getAllWrittenByPlaylists());
}

const S4PService = {
  getCurrentUser,
  getRecording,
  getRecordingAgeData,
  getRecordingGenderData,
  getRecordingTimelineStreams,
  getRecordingTimelineStreamsCsv,
  getSongwriterPageStatus,
  getSortedWorks,
  getStreamsByProductForWork,
  getTopPlaylists,
  getTrackEligibility,
  getWork,
  getWorkTimelineAnnotations,
  getWorkTimelineStreams,
  getWorkTimelineStreamsCsv,
  getWriter,
  getWriterTimelineStreams,
  getWriterTimelineStreamsCsv,
  getWriterWorksCsv,
  searchRecordings,
  searchWorks,
  searchWriters,
};

export default S4PService;
